import { Component, inject, OnInit, TemplateRef, viewChild, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, tap } from 'rxjs';
import { BaseComponent } from 'src/app/components/_base/base.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService, _ } from '@ngx-translate/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { captureException } from '@sentry/angular';
import { SearchService } from 'src/app/services/search/search.service';
import { SearchResult, SearchResultType, SearchResultTypeLabel } from 'src/app/types/search';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';

@Component({
  templateUrl: './search.component.html',
  standalone: true,
  selector: 'app-search',
  imports: [
    MatIconModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    TranslatePipe,
    MatMenuModule,
  ],
  styles: `
    .type-btn {
      --mdc-text-button-container-height: 32px;
      --mdc-filled-button-container-height: 32px;
      --mat-filled-button-horizontal-padding: 16px;
    }

    .item-btn {
      --mdc-text-button-container-height: 46px;
      --mdc-text-button-label-text-color: black;
    }
  `,
})
export class SearchComponent extends BaseComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly searchService = inject(SearchService);
  private readonly snackbar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  private _SHOW_LIMIT = 8;
  private _result: SearchResult[] = [];

  control: FormControl = this.fb.control('');
  loading = false;
  selectedType: SearchResultType | undefined;
  showingResult: SearchResult[] = [];
  types: { type: SearchResultType; count: number }[] = [];
  isShowFull = false;

  readonly SearchResultTypeLabel = SearchResultTypeLabel;

  @ViewChild('resultTemplate') resultTemplate: TemplateRef<unknown> | undefined;

  readonly menuTrigger = viewChild.required(MatMenuTrigger);

  ngOnInit(): void {
    this.sub$ = this.control.valueChanges
      .pipe(
        tap(() => {
          this.loading = true;
        }),
        debounceTime(1000),
      )
      .subscribe((val) => {
        if (!this.resultTemplate) return;

        this.menuTrigger().openMenu();
        // this.menu.open(this.searchInput.inputRef.nativeElement, this.resultTemplate, {
        //   position: PopupPosition['bottom-center'],
        // });
        this.searchService.query(val).subscribe({
          next: (result) => {
            this.loading = false;
            this._result = result;
            this.types = this._result.reduce<{ type: SearchResultType; count: number }[]>((result, item) => {
              const typeCountIndex = result.findIndex((_typeCount) => _typeCount.type === item.type);

              if (typeCountIndex === -1) result.push({ type: item.type, count: 1 });
              else result[typeCountIndex].count++;

              return result;
            }, []);

            this.update();
          },
          error: (error) => {
            captureException(error);
            this.loading = false;
            this.snackbar.open(
              this.translate.instant(_('error.error-occured')),
              this.translate.instant(_('common.close')),
              { panelClass: 'snackbar-error' },
            );
          },
        });
      });
  }

  get showingLeft() {
    const remain = this._result.filter((result) => (this.selectedType ? result.type === this.selectedType : true));
    return Math.max(remain.length - this.showingResult.length, 0);
  }

  get SHOW_LIMIT() {
    return this._SHOW_LIMIT;
  }

  // getFilterButtonColor(type: SearchResultType) {
  //   return this.selectedType === type ? ButtonColor.primary : ButtonColor.transparent;
  // }

  onSelectFilterType(type: SearchResultType) {
    if (this.selectedType === type) this.selectedType = undefined;
    else this.selectedType = type;

    this.update();
  }

  onShowFull() {
    this.isShowFull = !this.isShowFull;

    this.update();
  }

  update() {
    this.showingResult = this._result
      .filter((result) => (this.selectedType ? result.type === this.selectedType : true))
      .slice(0, this.isShowFull ? undefined : this._SHOW_LIMIT);
  }
}
